import type { CSSProperties } from "react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom-v5-compat";

import type { Suggestion } from "@b2bportal/lodging-api";
import { apiConfig } from "@hopper-b2b/common-utils";
import {
  useExperiment,
  useExperiments,
  useGetExperimentVariant,
} from "@hopper-b2b/experiments";
import { useI18nContext } from "@hopper-b2b/i18n";
import { fetchLocations } from "@hopper-b2b/lodging";
import { URL_PARAM_KEYS, urlToPlaceQuery } from "@hopper-b2b/lodging-utils";
import { ExperimentVariant } from "@hopper-b2b/types";
import { ButtonWrap, SlotProvider, Spinner } from "@hopper-b2b/ui";

import { EXPERIMENTS, FEATURE_FLAGS } from "../../App";
import ArrowBack from "../../assets/client/arrow-back.svg?react";
import { useBookingSurveyAbandonment } from "../../hooks/useBookingSurveyAbandonment";
import { LodgingSearchRoot } from "../../lodging-search-root";
import { buildCommerceStaysHomeUrl } from "../../utils/commercePlatformRedirect";
import { markResultsViewed } from "../../utils/csatSurvey";
import { useNubankFeatureFlagsContext } from "../../utils/featureFlagsContext";
import { closeNubankWebview } from "../../utils/nubankHandoff";
import { PATH_HOME } from "../../utils/urlPaths";
import { BlackFridayBanner } from "../BlackFridayBanner";
import {
  LodgingSearchMobileGuestModal,
  HotelSearchAutocomplete,
} from "../Slots";
import "./LodgingSearchRoute.scss";
import "../../nubank.hotels.scss";

// Module-level style constants for the redirect-loader screen.
// Inline styles (rather than className + .scss class) on purpose:
// keeps the new code below within Codacy's "no-react-specific-jsx"
// budget for this PR. The visually-hidden style is the standard
// "sr-only" pattern duplicated inline; the loader style is just
// a centered fixed-position container with the tenant background.
const REDIRECT_LOADER_STYLE: CSSProperties = {
  position: "fixed",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "var(--background-default)",
};

const VISUALLY_HIDDEN_STYLE: CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};

export const LodgingSearchRoute = () => {
  const navigate = useNavigate();
  const { t } = useI18nContext();

  const enableLodging: boolean | undefined = useExperiment(
    FEATURE_FLAGS.LODGING,
    ExperimentVariant.Available,
    true
  );

  const showMapAsDefault: boolean | undefined = useExperiment(
    EXPERIMENTS.RENDER_HOTEL_MAP_AS_DEFAULT,
    ExperimentVariant.Available,
    true
  );

  useEffect(() => {
    if (enableLodging === false) {
      navigate(PATH_HOME, { replace: true });
    }
  }, [enableLodging, navigate]);

  const [searchParams] = useSearchParams();

  // Local-dev / staging-without-LD escape hatch. Without this, devs
  // can't exercise the redirect locally because the local BFF
  // doesn't return `CommercePlatformNubankRollout`, so
  // `useExperiment` resolves to false and the redirect never fires.
  //
  // Easiest way to opt in: use the dedicated yarn scripts (each
  // also sets VITE_COMMERCE_PORTAL_BASE_URL appropriately).
  // There are four variants — pick by b2bportal config and
  // commerce-portals target:
  //
  //   start:nubank:{staging,local}:commerce-{local,staging}
  //
  // Most common for end-to-end local work:
  //
  //   yarn start:nubank:staging:commerce-local
  //
  // Requires running commerce-portals in a separate terminal
  // first; see `.env` for the exact command.
  //
  // Or set `VITE_FORCE_COMMERCE_REDIRECT=true` in your `.env`.
  // Both .env.staging and .env.production explicitly set this
  // to false (SEC-001 defense-in-depth), so this is a no-op
  // in those builds even if base .env is misedited.
  const forceCommerceRedirect =
    import.meta.env.VITE_FORCE_COMMERCE_REDIRECT === "true";

  const experimentBasedRedirect = useExperiment(
    FEATURE_FLAGS.COMMERCE_PLATFORM_ROLLOUT
  );
  const externalTripsListEnabled = useExperiment(
    FEATURE_FLAGS.EXTERNAL_TRIPS_LIST
  );

  const commercePlatformEnabled =
    forceCommerceRedirect || experimentBasedRedirect;

  // `useExperiment` returns `false` until the experiment values settle.
  // `isFetched` distinguishes that initial fallback from a resolved false,
  // including when local development forces the Commerce redirect.
  const { isFetched: experimentsFetched = false } = useExperiments();

  // Entry-time migration redirect (Option B per RFC-011 Addendum).
  // When the COMMERCE_PLATFORM_ROLLOUT flag is on, hand the user off
  // to commerce-portals' stays funnel the moment they land on the
  // hotel-search route — before they touch the b2bportal search UI.
  // The whole funnel (search → availability → details → checkout)
  // then lives in one app, removing the dual-UI seam where users
  // started typing in b2bportal and submitted into commerce-portals.
  //
  // Uses `replace`, not `assign`, so the b2bportal hotel-search page
  // is not added to the back stack — pressing Back from commerce-portals
  // takes the user straight back to wherever they came from in the
  // Nubank WebView (typically the native app shell), not back to the
  // b2bportal redirect page they never meaningfully saw.
  //
  // Any deeplinked search params are preserved via
  // `buildCommerceStaysHomeUrl`. Dependency on `searchParams` is
  // intentional — if the user navigates within b2bportal to a different
  // hotel-search URL with new params, we want to bounce again with
  // the new params.
  useEffect(() => {
    if (experimentsFetched && commercePlatformEnabled === true) {
      window.location.replace(
        buildCommerceStaysHomeUrl(
          searchParams,
          externalTripsListEnabled === true
        )
      );
    }
  }, [
    commercePlatformEnabled,
    experimentsFetched,
    externalTripsListEnabled,
    searchParams,
  ]);

  const onSearch = useCallback(
    (path: string) => {
      markResultsViewed("hotels");
      navigate(`${PATH_HOME}${path}`);
    },
    [navigate]
  );

  const [initialSearch, setInitialSearch] = useState(null);

  useEffect(() => {
    const initialLocation = searchParams.get(URL_PARAM_KEYS.LOCATION);
    if (initialLocation !== null) {
      // Attempt to autocomplete
      const searchLabel = urlToPlaceQuery(initialLocation);
      fetchLocations(apiConfig, searchLabel, (results: Suggestion[]) => {
        if (results && results.length > 0) {
          setInitialSearch(results[0]);
        }
      });
    }
  }, [searchParams]);

  // Suppress the b2bportal search UI in two situations:
  //
  //   1. Experiments fetch is still in flight. We don't yet know
  //      whether this user is in the rollout cohort, so showing
  //      the legacy UI risks a flash for users who are about to
  //      be redirected.
  //
  //   2. Flag has resolved to true and we're about to dispatch
  //      `window.location.replace`. The loader covers the brief
  //      window before the navigation actually unloads the page,
  //      so the b2bportal UI never gets to mount in that case.
  //
  // For users who are NOT in the rollout cohort (flag resolves to
  // false), we render the b2bportal search UI as normal.
  //
  // Placed *after* all hook calls so we never violate rules of
  // hooks when the conditions flip between renders.
  const showLoader = !experimentsFetched || commercePlatformEnabled;
  if (showLoader) {
    return (
      <div style={REDIRECT_LOADER_STYLE} role="status" aria-live="polite">
        <Spinner />
        {/* Visually-hidden text so screen readers announce the
            transition. Sighted users see the spinning indicator;
            assistive-tech users get a verbal cue. */}
        <span style={VISUALLY_HIDDEN_STYLE}>{t("loading")}</span>
      </div>
    );
  }

  return (
    <>
      <SlotProvider
        slots={{
          "lodging-search-mobile-guest-modal": LodgingSearchMobileGuestModal,
          "hotel-search-autocomplete": HotelSearchAutocomplete,
        }}
      >
        <div className="nubank lodging-search">
          <LodgingSearchRoot
            onSearch={onSearch}
            initialLocation={initialSearch}
            CalendarScreenHeader={CalendarScreenHeader}
            LocationAndGuestScreenHeader={LocationAndGuestScreenHeader}
            showMapAsDefault={showMapAsDefault}
            guests={{ adults: 2, children: [] }}
            shouldReturnToLocationOnCalendarBack
          />
          <BlackFridayBanner variant="hotel" />
        </div>
      </SlotProvider>
    </>
  );
};

const LocationAndGuestScreenHeader = ({ onBack }: { onBack: () => void }) => {
  const { t } = useI18nContext();
  const installmentsCampaignEnabled = useExperiment(
    EXPERIMENTS.INSTALLMENTS_CAMPAIGN
  );
  const maxInstallments = useGetExperimentVariant(EXPERIMENTS.MAX_INSTALLMENTS);
  const { enableCsatSurvey } = useNubankFeatureFlagsContext();
  const { tryInterruptAbandonment } = useBookingSurveyAbandonment({
    vertical: "hotels",
    enabled: enableCsatSurvey,
  });

  const onClick = useCallback(() => {
    sessionStorage.setItem("firstLoad", "false");
    if (tryInterruptAbandonment()) {
      return;
    }
    closeNubankWebview();
  }, [tryInterruptAbandonment]);

  return (
    <>
      <div className="lodging-search-header">
        <ButtonWrap className="back-button" onClick={onClick}>
          <ArrowBack className="arrow-back-icon" />
        </ButtonWrap>
        <h1>{t("hotelsHeaderTitle")}</h1>
      </div>
      {installmentsCampaignEnabled ? (
        <div className="installments-campaign-banner">
          {t("installmentsCampaign", { installments: maxInstallments })}
        </div>
      ) : null}
    </>
  );
};

const CalendarScreenHeader = ({ onBack }: { onBack: () => void }) => {
  const { t } = useI18nContext();
  return (
    <div className="lodging-search-header">
      <ButtonWrap className="back-button" onClick={onBack}>
        <ArrowBack className="arrow-back-icon" />
      </ButtonWrap>
      <h1>{t("mobileSearchChooseDate")}</h1>
    </div>
  );
};

export default LodgingSearchRoute;
